import React, { useState, useEffect } from "react";
import { useProjectContext } from "../../context/useProjectContext";
import { useLicensingContext } from "../../context/useLicensingContext";
import { useUserContext } from "../../context/useUserContext";
import { showGenericError } from "../../helper/Notifications";
import useMemberProductAccess from "../../hooks/useMemberProductAccess";
import SecondaryButton from "../../Components/Generic/SecondaryButton";
import LocalStorage from "../../helper/LocalStorage";
import SeatManagementPage from "../../Components/SeatManagement/SeatManagementPage";
import QuickStartSection from "../../Components/SeatManagement/QuickStartSection";

const InfoIcon = (props) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M10 13.3332V9.99984M10 6.6665H10.0084M18.3334 9.99984C18.3334 14.6022 14.6024 18.3332 10 18.3332C5.39765 18.3332 1.66669 14.6022 1.66669 9.99984C1.66669 5.39746 5.39765 1.6665 10 1.6665C14.6024 1.6665 18.3334 5.39746 18.3334 9.99984Z" stroke="#6941C6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UsersPage = () => {
  const { selectedProject, meetingsUsersCount } = useProjectContext();
  const { subscription } = useLicensingContext();
  const { user, updateUser } = useUserContext();
  const { hasProductAccess: hasMemberMeetingsAccess, currentMembership, isAdmin } = useMemberProductAccess("meetings");

  const isWebsiteTranslationSubscription =
    subscription?.items?.length > 0 &&
    subscription.items.every((i) => i.product_slug === "website_translation");

  const [showQuickStart, setShowQuickStart] = useState(() =>
    selectedProject ? !LocalStorage.getQuickStartDismissed(selectedProject.id) : true
  );
  const [downloadAcknowledged, setDownloadAcknowledged] = useState(() =>
    Boolean(user?.app_download_confirmed_at)
  );
  const [isConfirmingDownload, setIsConfirmingDownload] = useState(false);

  const downloadAppsCheckboxId = React.useId();
  const hasConfirmedDownload = Boolean(user?.app_download_confirmed_at);
  const isDownloadCheckboxDisabled = downloadAcknowledged || hasConfirmedDownload || isConfirmingDownload;
  const downloadStepComplete = hasConfirmedDownload || downloadAcknowledged;

  const openQuickStartGuide = () => {
    LocalStorage.setQuickStartDismissed(selectedProject.id, false);
    setShowQuickStart(true);
  };

  const handleDismissGuide = () => {
    setShowQuickStart(false);
    LocalStorage.setQuickStartDismissed(selectedProject.id);
  };

  const handleDownloadAcknowledgementChange = (e) => {
    if (!e.target.checked || isDownloadCheckboxDisabled) return;
    setDownloadAcknowledged(true);
    setIsConfirmingDownload(true);
    updateUser(
      { app_download_confirmed: true },
      () => setIsConfirmingDownload(false),
      () => { setIsConfirmingDownload(false); setDownloadAcknowledged(false); showGenericError(); }
    );
  };

  useEffect(() => {
    if (hasConfirmedDownload) { setDownloadAcknowledged(true); return; }
    if (!isConfirmingDownload) setDownloadAcknowledged(false);
  }, [hasConfirmedDownload, isConfirmingDownload]);

  useEffect(() => {
    if (selectedProject) {
      setShowQuickStart(!LocalStorage.getQuickStartDismissed(selectedProject.id));
    }
  }, [selectedProject?.id]);

  useEffect(() => {
    if (subscription === undefined) return;
    if (!LocalStorage.getQuickStartDismissed(selectedProject?.id)) {
      setShowQuickStart(Boolean(subscription?.items?.some((i) => i.product_slug === "meetings")));
    }
  }, [subscription]);

  return (
    <SeatManagementPage
      productSlug="meetings"
      title="Meetings"
      noAccessTitle="Meetings Plans"
      subtitle="Manage seats, monitor usage, and get started with real-time AI interpretation across your team."
      noAccessSubtitle="Choose a plan to unlock real-time AI interpretation and start managing seats for your team."
      deleteModalBody="Deleting this seat will immediately remove the user's access to the platform (web, mobile, Teams, Slack, desktop) and permanently delete their account and all associated data."
      bulkDeletePlatformName="the platform"
      emptyStateText="Invite users who can access the services included in your subscription. Use the email addresses they log in to emotii with."
      extraColumns={[
        { key: "video", label: "Video min", getFromItem: (usage) => usage.video ?? 0 },
      ]}
      showRestrictedCard={isWebsiteTranslationSubscription}
      headerExtra={() =>
        subscription && !showQuickStart && subscription?.items?.some((i) => i.product_slug === "meetings")
          ? <SecondaryButton variant="brandTint" icon={<InfoIcon />} text="How to use emotii.ai" onClick={openQuickStartGuide} />
          : null
      }
      quickStartSection={({ openAddSeats, isPageLoading }) =>
        !isPageLoading && showQuickStart && (!currentMembership || isAdmin) &&
        subscription?.items?.some((i) => i.product_slug === "meetings") && (
          <QuickStartSection
            subscription={subscription}
            usersCount={meetingsUsersCount}
            downloadAcknowledged={downloadAcknowledged}
            isConfirmingDownload={isConfirmingDownload}
            downloadStepComplete={downloadStepComplete}
            downloadAppsCheckboxId={downloadAppsCheckboxId}
            isDownloadCheckboxDisabled={isDownloadCheckboxDisabled}
            handleDismissGuide={handleDismissGuide}
            handleDownloadAcknowledgementChange={handleDownloadAcknowledgementChange}
            onAddSeats={openAddSeats}
          />
        )
      }
    />
  );
};

export default UsersPage;